import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { Element } from '../element.model';
import { environment } from '../environments/environment';
import { map, catchError, tap } from 'rxjs/operators';

export interface PagedResult<T> {
  items: T[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

@Injectable({
  providedIn: 'root'
})
export class DataService {
  private apiUrl = environment.apiBaseUrl + '/elemente'; // API-Pfad anpassen
  private currentPageSubject = new BehaviorSubject<number>(1);
  private pageSizeSubject = new BehaviorSubject<number>(20);
  private searchTermSubject = new BehaviorSubject<string | null>(null);
  private lagerFilterSubject = new BehaviorSubject<string | null>(null);
  private kategorieFilterSubject = new BehaviorSubject<string | null>(null);
  private herstellerFilterSubject = new BehaviorSubject<string | null>(null);
  private lagerortFilterSubject = new BehaviorSubject<string | null>(null);
  private sortColumnSubject = new BehaviorSubject<string>('name');
  private sortDirectionSubject = new BehaviorSubject<string>('asc');

  // Observable streams
  public currentPage$ = this.currentPageSubject.asObservable();
  public pageSize$ = this.pageSizeSubject.asObservable();

  constructor(private http: HttpClient) { }

  /**
   * Normalisiert einen String, indem Leerzeichen, Bindestriche und andere 
   * nicht-alphanumerische Zeichen entfernt werden
   */
  private normalizeString(input: string): string {
    if (!input) return '';
    return input.toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  /**
   * Holt paginierte Elemente mit optionalen Filtern
   */
  getElementePaged(
    page: number = 1,
    pageSize: number = 20,
    searchTerm: string | null = null,
    lager: string | null = null,
    kategorie: string | null = null,
    hersteller: string | null = null,
    lagerort: string | null = null,
    sortColumn: string = 'name',
    sortDirection: string = 'asc'
  ): Observable<PagedResult<Element>> {
    console.log('DataService: getElementePaged called with params:',
      { page, pageSize, searchTerm, lager, kategorie, hersteller, lagerort, sortColumn, sortDirection });

    // Update subjects
    this.currentPageSubject.next(page);
    this.pageSizeSubject.next(pageSize);
    this.searchTermSubject.next(searchTerm);
    this.lagerFilterSubject.next(lager);
    this.kategorieFilterSubject.next(kategorie);
    this.herstellerFilterSubject.next(hersteller);
    this.lagerortFilterSubject.next(lagerort);
    this.sortColumnSubject.next(sortColumn);
    this.sortDirectionSubject.next(sortDirection);

    // Build query parameters
    let params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    // Füge alle optionalen Parameter hinzu, aber nur wenn sie einen Wert haben
    if (searchTerm) {
      params = params.set('searchTerm', searchTerm);
    }

    if (lager && lager.toLowerCase() !== 'alle') {
      params = params.set('lager', lager);
    }

    if (kategorie) {
      params = params.set('kategorie', kategorie);
      console.log('Setting kategorie parameter:', kategorie);
    }

    if (hersteller) {
      params = params.set('hersteller', hersteller);
      console.log('Setting hersteller parameter:', hersteller);
    }

    if (lagerort) {
      params = params.set('lagerort', lagerort);
      console.log('Setting lagerort parameter:', lagerort);
    }

    if (sortColumn) {
      params = params.set('sortColumn', sortColumn);
    }

    if (sortDirection) {
      params = params.set('sortDirection', sortDirection);
    }

    // Debug: Log actual HTTP parameters
    console.log('API request URL:', `${this.apiUrl}/paged`);
    console.log('API request params:', params.toString());

    // Make HTTP request with parameters
    return this.http.get<PagedResult<Element>>(`${this.apiUrl}/paged`, { params }).pipe(
      tap(response => console.log('API response received:', response)),
      map(result => ({
        ...result,
        items: result.items.map(element => ({
          ...element,
          lager_lagerort: `${element.lager || '-'} / ${element.lagerort || '-'}`
        }))
      })),
      catchError(error => {
        console.error('Error fetching paged elements:', error);
        console.error('Error details:', error.status, error.statusText, error.message);
        if (error.error) {
          console.error('Server error:', error.error);
        }
        return of({
          items: [],
          totalCount: 0,
          page: 1,
          pageSize: pageSize,
          totalPages: 0
        });
      })
    );
  }

  /**
   * Aktualisiert die aktuelle Seite mit den aktuellen Filtern
   */
  refreshCurrentPage(): Observable<PagedResult<Element>> {
    return this.getElementePaged(
      this.currentPageSubject.value,
      this.pageSizeSubject.value,
      this.searchTermSubject.value,
      this.lagerFilterSubject.value,
      this.kategorieFilterSubject.value,
      this.herstellerFilterSubject.value,
      this.lagerortFilterSubject.value,
      this.sortColumnSubject.value,
      this.sortDirectionSubject.value
    );
  }

  /**
   * Holt alle Elemente (veraltet - besser getElementePaged nutzen)
   * Beibehalten für Abwärtskompatibilität
   */
  getElemente(): Observable<Element[]> {
    return this.http.get<Element[]>(this.apiUrl).pipe(
      map(elements => elements.map(element => ({
        ...element,
        lager_lagerort: `${element.lager || '-'} / ${element.lagerort || '-'}`
      }))),
      catchError(error => {
        console.error('Error fetching all elements:', error);
        return of([]);
      })
    );
  }

  /**
   * Holt alle verfügbaren Kategorien
   */
  getCategories(): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/Categories`).pipe(
      catchError(error => {
        console.error('Error fetching categories:', error);
        return of([]);
      })
    );
  }

  /**
   * Holt alle verfügbaren Lager
   */
  getWarehouses(): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/Warehouses`).pipe(
      catchError(error => {
        console.error('Error fetching warehouses:', error);
        return of([]);
      })
    );
  }

  /**
   * Fügt ein neues Element hinzu
   */
  addElement(element: Element): Observable<Element> {
    return this.http.post<Element>(this.apiUrl, element).pipe(
      catchError(error => {
        console.error('Error adding element:', error);
        throw error;
      })
    );
  }

  /**
   * Löscht ein Element anhand seiner ID
   */
  deleteElement(id: number): Observable<any> {
    const url = `${this.apiUrl}/${id}`;
    return this.http.delete(url).pipe(
      catchError(error => {
        console.error('Error deleting element:', error);
        throw error;
      })
    );
  }

  /**
   * Holt ein einzelnes Element anhand seiner ID
   */
  getElement(id: number): Observable<Element> {
    const url = `${this.apiUrl}/${id}`;
    return this.http.get<Element>(url).pipe(
      catchError(error => {
        console.error('Error fetching element:', error);
        throw error;
      })
    );
  }

  /**
   * Holt alle Lagerorte für ein bestimmtes Lager
   */
  getLagerorte(lager: string): Observable<string[]> {
    const url = `${this.apiUrl}/Lagerort/${encodeURIComponent(lager)}`;
    return this.http.get<string[]>(url).pipe(
      catchError(error => {
        console.error('Error fetching lagerorte:', error);
        return of([]);
      })
    );
  }

  /**
   * Aktualisiert ein Element
   */
  updateElement(id: number, element: Element): Observable<any> {
    const url = `${this.apiUrl}/${id}`;
    return this.http.put<Element>(url, element).pipe(
      catchError(error => {
        console.error('Error updating element:', error);
        throw error;
      })
    );
  }

  /**
   * Lädt ein Bild für ein Element hoch
   */
  uploadFile(file: File, elementId: number): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('elementId', elementId.toString());

    return this.http.post(`${this.apiUrl}/image/upload`, formData).pipe(
      catchError(error => {
        console.error('Error uploading file:', error);
        throw error;
      })
    );
  }

  /**
   * Holt ein Bild für ein Element
   */
  getFile(elementId: number): Observable<any> {
    const url = `${this.apiUrl}/image/${elementId}`;
    return this.http.get(url, { responseType: 'blob' }).pipe(
      catchError(error => {
        console.error('Error fetching file:', error);
        throw error;
      })
    );
  }

  /**
   * Hilfsmethode für clientseitige Filterung (falls benötigt)
   * Kann für lokale Filterung in der Komponente verwendet werden
   */
  applyNormalizedFilter(elements: Element[], searchTerm: string | null): Element[] {
    if (!searchTerm) return elements;

    const normalizedSearchTerm = this.normalizeString(searchTerm);

    return elements.filter(element =>
      (element.name && this.normalizeString(element.name).includes(normalizedSearchTerm)) ||
      (element.description && this.normalizeString(element.description).includes(normalizedSearchTerm)) ||
      (element.hersteller && this.normalizeString(element.hersteller).includes(normalizedSearchTerm)) ||
      (element.bestellnumer && this.normalizeString(element.bestellnumer).includes(normalizedSearchTerm))
    );
  }
}
